import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Clock, Building2, MapPin, DollarSign, Users, TrendingUp, FileText, Briefcase, Target, MessageSquare, Brain, CheckCircle, AlertCircle, ExternalLink, Plus, X, Loader } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageHeader from './components/PageHeader';
import InterviewDetails from './components/InterviewDetails';
import PracticeSession from './components/PracticeSession';
import CompanyInfo from './components/CompanyInfo';
import InterviewPreparationReport from './components/InterviewPreparationReport';
import MockScheduleModal from './components/MockScheduleModal';
import PracticeTimeModal from './components/PracticeTimeModal';
import LoadingModal from './components/LoadingModal';
import apiRequest from "@/services/requests";




const RealInterviewDetails = () => {
const { id } = useParams();
const navigate = useNavigate();
const [interview, setInterview] = useState(null);
const [isLoading, setIsLoading] = useState(true);
const [showMockScheduleModal, setShowMockScheduleModal] = useState(false);
const [showPracticeTimeModal, setShowPracticeTimeModal] = useState(false);
const [showLoadingModal, setShowLoadingModal] = useState(false);
const [loadingProgress, setLoadingProgress] = useState(0);
const [loadingMessage, setLoadingMessage] = useState('');
const [mockInterviewDate, setMockInterviewDate] = useState('');
const [mockInterviewTime, setMockInterviewTime] = useState('');
const [isScheduling, setIsScheduling] = useState(false);
const [isReschedule, setIsReschedule] = useState(false);


useEffect(() => {
loadInterview();
}, [id]);



const loadInterview = async () => {
setIsLoading(true);
try {
const response = await apiRequest.interviewDetails(id);
setInterview(response);
} catch (error) {
console.error('Failed to load interview details:', error);
toast.error('Could not load interview details');
navigate('/interviews');
} finally {
setIsLoading(false);
}
};



const suggestPracticeTimes = () => {
if (!interview) return [];

const interviewDate = new Date(interview.date + 'T' + interview.time);
const suggestions = [];

 // 3 days before
const threeDaysBefore = new Date(interviewDate);
threeDaysBefore.setDate(threeDaysBefore.getDate() - 3);
suggestions.push({
date: threeDaysBefore.toISOString().split('T')[0],
time: '10:00',
label: '3 days before - Morning'
});

// 2 days before
const twoDaysBefore = new Date(interviewDate);
twoDaysBefore.setDate(twoDaysBefore.getDate() - 2);
suggestions.push({
date: twoDaysBefore.toISOString().split('T')[0],
time: '14:00',
label: '2 days before - Afternoon'
});

// 1 day before
const oneDayBefore = new Date(interviewDate);
oneDayBefore.setDate(oneDayBefore.getDate() - 1);
suggestions.push({
date: oneDayBefore.toISOString().split('T')[0],
time: '16:00',
label: '1 day before - Evening'
});

return suggestions;
};



const handleScheduleMockInterview = () => {
setShowMockScheduleModal(false);
setShowPracticeTimeModal(true);
};




const handleConfirmMockSchedule = async (suggestion) => {
 if (isScheduling) return;

setMockInterviewDate(suggestion.date);
setMockInterviewTime(suggestion.time);
setShowPracticeTimeModal(false);
setShowLoadingModal(true);
setLoadingProgress(0);
setIsScheduling(true);

const messages = [
"Analyzing interview requirements...",
"Generating practice questions...",
"Preparing mock interview scenario...",
"Setting up AI interviewer...",
"Finalizing practice session..."
];

try{
// Progress through loading messages
for (let i = 0; i < messages.length; i++) {
setLoadingMessage(messages[i]);
await new Promise(resolve => setTimeout(resolve, 1200));
setLoadingProgress(((i + 1) / messages.length) * 100);
}

// Prepare mock interview data
const mockInterviewData = {
linked_real_interview_id: interview.id,
job_title: interview.jobTitle, 
company_name: interview.company,
company_logo_url: interview.companyLogo,
scheduled_date: suggestion.date,
scheduled_time: suggestion.time + ':00', // Ensure format HH:MM:SS
real_interview_scheduled_date:interview.scheduled_date
};

// Call API to create mock interview
const response = await apiRequest.createPreparationInterview(mockInterviewData);

// setInterview({
// ...interview,
// mockPracticeSession: {
// id: response.id,
// scheduled: true,
// date: suggestion.date,
// time: suggestion.time,
// status: 'scheduled',
// remindersSent: false
// }
// });

setShowLoadingModal(false);
toast.success('Mock interview scheduled successfully!', {
position: 'top-right',
autoClose: 3000
});

// Reload interview details to get fresh data
await loadInterview();
} catch (error) {
console.error('Failed to schedule mock interview:', error);
setShowLoadingModal(false);
toast.error('Failed to schedule mock interview. Please try again.', {
position: 'top-right',
autoClose: 5000
});
} finally {
setIsScheduling(false);
}
};

 

const handleCancelMockInterview = async () => {
if (!interview?.mockPracticeSession?.id) return;
try{
await apiRequest.updateInterview(interview.mockPracticeSession.id, {status:'cancelled'});
setInterview({
...interview,
mockPracticeSession: null
});

toast.success('Mock interview cancelled successfully', {
position: 'top-right'
});

// Reload to get fresh data
await loadInterview();

} catch (error) {
console.error('Failed to cancel mock interview:', error);
toast.error('Failed to cancel mock interview', {
position: 'top-right'
});
}
};


const handleRescheduleMockInterview = async (newSuggestion) => {
if (!interview?.mockPracticeSession?.id) return;

setShowPracticeTimeModal(false);
try{
const updateData = {
scheduled_date: newSuggestion.date,
scheduled_time: newSuggestion.time + ':00',
status: 'rescheduled'
};

await apiRequest.updateInterview(interview.mockPracticeSession.id, updateData);

setInterview({
...interview,
mockPracticeSession: {
...interview.mockPracticeSession,
date: newSuggestion.date,
time: newSuggestion.time,
status: 'rescheduled'
}
});

toast.success('Mock interview rescheduled successfully', {
position: 'top-right'
});

await loadInterview();


}catch (error) {
console.error('Failed to reschedule mock interview:', error);
toast.error('Failed to reschedule mock interview', {
position: 'top-right'
});
} finally{
setIsReschedule(false)
}
};



// if (isLoading) {
// return (
// <div className="min-h-screen bg-gray-50 flex items-center justify-center">
// <div className="text-center">
// <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
// <p className="text-gray-600">Loading interview details...</p>
// </div>
// </div>
// );
// }


if (isLoading) {
return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
<div className="flex items-center justify-center h-64">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
</div>
</div>
</main>
</div>
);
}

// if (!interview) {
// return (
// <div className="min-h-screen bg-gray-50 flex items-center justify-center">
// <div className="text-center">
// <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
// <p className="text-gray-600">Failed to load interview details</p>
// </div>
// </div>
// );
// }


if (!interview) {
return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
<div className="text-center">
<p className="text-text-secondary">Interview not found</p>
<Button
onClick={() => navigate('/interviews')}
className="mt-4"
>
Back to Interviews
</Button>
</div>
</div>
</main>
</div>
);
}




return (
<div className="min-h-screen bg-background">
<Header />




<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">


{/* Page Header */}
<PageHeader 
interview={interview}
setShowMockScheduleModal={setShowMockScheduleModal}
/>


<div className="space-y-6">

{/* Interview Details */}
<InterviewDetails 
interview={interview} 
/>

{/* Mock Practice Session */}
{interview.mockPracticeSession?.scheduled && (
<PracticeSession
interview={interview} 
handleCancelMockInterview={handleCancelMockInterview}
handleRescheduleMockInterview={handleRescheduleMockInterview}
setShowPracticeTimeModal={setShowPracticeTimeModal}
setIsReschedule={setIsReschedule} 
navigate={navigate}
/>
)}

{/* Company Information */}
<CompanyInfo 
interview={interview}
/>

{/* Interview Preparation Report */}
<InterviewPreparationReport 
interview={interview}
/>






</div>
</div>
</main>

{/* Mock Schedule Modal */}
{showMockScheduleModal && (
<MockScheduleModal 
setShowMockScheduleModal={setShowMockScheduleModal}
interview={interview}
handleScheduleMockInterview={handleScheduleMockInterview}
/>
)}

{/* Practice Time Selection Modal */}
{showPracticeTimeModal && (
<PracticeTimeModal
interview={interview}
suggestPracticeTimes={suggestPracticeTimes}
handleConfirmMockSchedule={handleConfirmMockSchedule}
handleRescheduleMockInterview={handleRescheduleMockInterview}
setShowPracticeTimeModal={setShowPracticeTimeModal}
isReschedule={isReschedule}
setIsReschedule={setIsReschedule} 
/>
)}

{/* Loading Modal */}
{showLoadingModal && (
<LoadingModal
loadingMessage={loadingMessage}
loadingProgress={loadingProgress}
 />
)}
</div>
);
};

export default RealInterviewDetails;